"use client";
import SectionTitle from "@/shared/app/ui/text/PageSectionTitle";
import { faPlus } from "@fortawesome/pro-light-svg-icons";
import { faExternalLinkAlt } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

interface WebItem {
  id: number;
  title: string;
  subtitle: string;
  logo: string;
  bgColor: string;
  url?: string;
}

interface WebListProps {
  items: WebItem[];
}

const WebList: React.FC<WebListProps> = ({ items }) => {
  const [visibleCount, setVisibleCount] = useState(6);

  const handleLoadMore = () => setVisibleCount((prev) => prev + 6);

  const visibleItems = items.slice(0, visibleCount);

  return (
    <div className="w-full mx-auto mt-40">
      {/* 리스트 */}
      <ul className="grid grid-cols-1 lg:grid-cols-3 md:grid-cols-2 gap-8">
        {visibleItems.map((item, i) => {
          if (i < 3) {
            return (
              <li key={item.id}>
                <div className="block group flex flex-col overflow-hidden bg-white relative">
                  <div className="relative w-full aspect-[446/300] md:aspect-[446/350] rounded-2xl overflow-hidden shadow-2xs border border-light-10  transition-all duration-500 group-hover:shadow-xl group-hover:-translate-y-1">
                    <Image
                      src={item.logo}
                      alt={item.title}
                      fill
                      className="object-cover transition-transform duration-500 hover:scale-105"
                    />
                  </div>

                  {/* 하단 텍스트 영역 */}
                  <div className="md:pt-7 pt-4 px-2 md:px-5 flex flex-col items-start md:gap-1 gap-0.5">
                    <div className="flex items-center gap-2">
                      <p
                        className="md:text-xl text-base text-black-100 tracking-tighter"
                        style={{ fontFamily: '"pre-SemiBold", sans-serif' }}
                      >
                        {item.title}
                      </p>
                      {item.url && (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-black-300 hover:text-midGray-50 transition-colors md:text-base text-[15px]"
                        >
                          <FontAwesomeIcon icon={faExternalLinkAlt} />
                        </a>
                      )}
                    </div>

                    <p
                      className="text-base text-lightGray-20 tracking-tight leading-6 mt-1"
                      style={{ fontFamily: '"pre-Regular", sans-serif' }}
                      dangerouslySetInnerHTML={{ __html: item.subtitle }}
                    />
                  </div>
                </div>
              </li>
            );
          }

          return (
            <motion.li
              key={item.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: i * 0.05 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <motion.div
                className="block group flex flex-col overflow-hidden bg-white relative"
                whileHover={{ scale: 1.02 }}
              >
                <div className="relative w-full aspect-[446/300] md:aspect-[446/350] rounded-2xl overflow-hidden shadow-2xs border border-light-10  transition-all duration-500 group-hover:shadow-xl group-hover:-translate-y-1">
                  <Image
                    src={item.logo}
                    alt={item.title}
                    fill
                    className="object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>

                {/* 하단 텍스트 영역 */}
                <div className="md:pt-7 pt-4 px-2 md:px-5 flex flex-col items-start md:gap-1 gap-0.5">
                  <div className="flex items-center gap-2">
                    <p
                      className="md:text-xl text-base text-black-100 tracking-tighter"
                      style={{ fontFamily: '"pre-SemiBold", sans-serif' }}
                    >
                      {item.title}
                    </p>
                    {item.url && (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-black-300 hover:text-midGray-50 transition-colors md:text-base text-[15px]"
                      >
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                      </a>
                    )}
                  </div>

                  <p
                    className="text-base text-lightGray-20 tracking-tight leading-6 mt-1"
                    style={{ fontFamily: '"pre-Regular", sans-serif' }}
                    dangerouslySetInnerHTML={{ __html: item.subtitle }}
                  />
                </div>
              </motion.div>
            </motion.li>
          );
        })}
      </ul>

      {/* 더보기 버튼 */}
      {visibleCount < items.length && (
        <div className="flex justify-center mt-10">
          <motion.button
            whileHover={{ scale: 1.01, y: -1 }}
            whileTap={{ scale: 0.99, y: 0 }}
            onClick={handleLoadMore}
            className="group px-8 py-3 rounded-xl border border-[#D2D6DD] bg-white  transition-all duration-300 flex items-center gap-2 hover:shadow-2xs hover:border-[#BFC3CA]"
          >
            <span
              className="relative z-10 text-base tracking-tighter text-midGray-40 group-hover:text-midGray-50 transition-colors duration-300"
              style={{ fontFamily: '"one-r", sans-serif' }}
            >
              더보기
            </span>
            <FontAwesomeIcon
              icon={faPlus}
              className="text-base text-midGray-40 group-hover:text-midGray-50 group-hover:rotate-90 transition-all duration-500 ease-out"
            />
          </motion.button>
        </div>
      )}
    </div>
  );
};
export default function WebSection() {
  const websites: WebItem[] = [
    {
      id: 1,
      title: "대전 사회적경제 플랫폼",
      subtitle: "대전의 사회적경제기업 현황과,<br/>정보를 통합적으로 제공 ",
      logo: "/assets/images/page/service/web/web1.png",
      bgColor: "#E8F3FF",
      url: "https://djse.or.kr/app/main/index",
    },
    {
      id: 2,
      title: "충남 사회적경제 플랫폼",
      subtitle: "충남의 사회적경제기업 현황과<br/>정보를 통합적으로 제공",
      logo: "/assets/images/page/service/web/web2.png",
      bgColor: "#FFF3E8",
      url: "https://www.cnse.kr/app/main/index",
    },
    {
      id: 3,
      title: "충남사회적경제 경영지원플랫폼",
      subtitle: "충남의 사회적경제기업 상담과<br/>지원정보 및 자료 제공",
      logo: "/assets/images/page/service/web/web3.png",
      bgColor: "#E8FFF3",
      url: "https://seeat.cnse.kr/app/main/index",
    },
    {
      id: 4,
      title: "경남마을기업 VMS",
      subtitle: "온라인 의무교육, 수강여부 확인 및<br/>수료증 발급",
      logo: "/assets/images/page/service/web/web4.png",
      bgColor: "#FFF3E8",
    },
    {
      id: 5,
      title: "공동체공모사업",
      subtitle: "대전 공동체공모사업 사업신청부터<br/>최종보고까지 온라인 관리",
      logo: "/assets/images/page/service/web/web5.png",
      bgColor: "#E8FFF3",
    },
    {
      id: 6,
      title: "마을사람도서관",
      subtitle: "마을활동가 간 활동내역을 공유하고<br/>개별로 활동확인서 발급",
      logo: "/assets/images/page/service/web/web6.png",
      bgColor: "#E8F3FF",
    },
    {
      id: 7,
      title: "대전사회적경제연구원 사회적협동조합",
      subtitle: "지역사회와 함께하는 사회적경제 인사이트 <br/>& 알림 HUB.",
      logo: "/assets/images/page/service/web/web7.png",
      bgColor: "#EDEDE3",
    },
    {
      id: 8,
      title: "사회적협동조합 세상만사",
      subtitle:
        "공존과 협동을 바탕으로 조합원의 성장과 <br/>건강한 사회적경제 생태계를 지향합니다",
      logo: "/assets/images/page/service/web/web8.png",
      bgColor: "#FFE6F4",
      url: "http://www.sesang-mansa.kr/app/main/index",
    },
    {
      id: 9,
      title: "안산시마을만들기지원센터",
      subtitle: "든든한 마을, 단단한 공동체<br/>안산시마을만들기지원센터",
      logo: "/assets/images/page/service/web/web9.png",
      bgColor: "#ABE8FF",
      url: "http://www.happyansan.or.kr/app/main/index",
    },
    {
      id: 10,
      title: "대전광역시 사회적자본지원센터",
      subtitle: "지역공동체의 힘과 연대를 잇는 <br/>사회적자본 플랫폼",
      logo: "/assets/images/page/service/web/web10.png",
      bgColor: "#ABE8FF",
      //   url: "https://localbiz.co.kr",
    },
    {
      id: 11,
      title: "대전 동구 마을공동체 지원센터",
      subtitle:
        "사람과 정이 이어지는 동구, <br/>주민이 함께 만드는 따뜻한 마을공동체를 지원합니다",
      logo: "/assets/images/page/service/web/web11.png",
      bgColor: "#F7FDEF",
      url: "http://www.dcscenter.kr/app/main/index",
    },
    {
      id: 12,
      title: "대전 서구 마을공동체 지원센터",
      subtitle:
        "마을의 삶과 이야기를 연결하며,<br/>주민 중심의 활기찬 공동체를 만들어갑니다",
      logo: "/assets/images/page/service/web/web12.png",
      bgColor: "#F7FDEF",
    },
    {
      id: 13,
      title: "충남사회적경제네트워크",
      subtitle:
        "연대와 협동, 공생의 가치를 지키며 ,<br/>충남 사회적경제 생태계 구축과 지역화를 지원하는 비영리단체입니다.",
      logo: "/assets/images/page/service/web/web13.png",
      bgColor: "#CDEDFF",
      url: "http://www.sechungnam.org/app/main/index",
    },
    {
      id: 14,
      title: "한끼100원 나눔운동본부",
      subtitle:
        "배부른 나의 하루 속에서 가난하고 소외된 이웃을 생각하며, <br/>지속적인 나눔과 연대를 실천합니다.",
      logo: "/assets/images/page/service/web/web14.png",
      bgColor: "#FFD5D6",
      url: "http://100.djcaritas.or.kr/",
    },
    {
      id: 15,
      title: "한국에너지기술연구원 FEP 융합연구단",
      subtitle: "에너지기술로 행복사회를 열어가는<br/>한국에너지기술연구원",
      logo: "/assets/images/page/service/web/web15.png",
      bgColor: "#EDF2FF",
    },
    {
      id: 16,
      title: "안산시지속가능발전협의회",
      subtitle: "모두가 참여하고 책임지는<br/>지속가능한 사회를 실현합니다.",
      logo: "/assets/images/page/service/web/web16.png",
      bgColor: "#EFFFE0",
      url: "https://www.asag21.org",
    },
    {
      id: 17,
      title: "대전광역시 인권센터",
      subtitle: "대전지역의 건강한 인권문화<br/> 정착을 위해 노력합니다.",
      logo: "/assets/images/page/service/web/web17.png",
      bgColor: "#EFFFE0",
    },
    {
      id: 18,
      title: "지역아동센터 대전광역시지원단",
      subtitle: "지역사회 방과후 돌봄의 중심<br/>지역아동센터",
      logo: "/assets/images/page/service/web/web18.png",
      bgColor: "#EFFFE0",
      url: "http://www.djcareinfo.info/",
    },
    {
      id: 19,
      title: "여행예약 시스템 공감만세",
      subtitle: "공정함에 감동한 사람들이 <br/>만드는 세상",
      logo: "/assets/images/page/service/web/web19.png",
      bgColor: "#EFFFE0",
      url: "www.fairtravelkorea.com",
    },
    {
      id: 20,
      title: "대전깃대종 브랜딩",
      subtitle:
        "대전의 산·계곡·하천을 대표하는 생태 깃대종과<br/> 함께 건강한 도시 생태계를 만들어갑니다",
      logo: "/assets/images/page/service/web/web20.png",
      bgColor: "#EFFFE0",
    },
    {
      id: 21,
      title: "민들레의료복지사회적협동조합",
      subtitle:
        "주민과 의료·복지 종사자가 협력하여<br/>마을마다 건강생활공동체를 만들어가는 사회적협동조합입니다",
      logo: "/assets/images/page/service/web/web21.png",
      bgColor: "#EFFFE0",
      url: "http://www.mindlle.org/",
    },
    {
      id: 22,
      title: "장례고객관리 시스템 꽃잠",
      subtitle:
        "장례 절차를 통합 관리하고<br/>애도의 과정을 돕는 맞춤형 장례고객관리 시스템입니다",

      logo: "/assets/images/page/service/web/web22.png",
      bgColor: "#EFFFE0",
    },
    {
      id: 23,
      title: "공공구매 플랫폼 가치플러스",
      subtitle:
        "협업과 창업 지원을 통해 <br/>사회가치 실현과 건강한 지역 경제 생태계를 조성합니다",
      logo: "/assets/images/page/service/web/web23.png",
      bgColor: "#EFFFE0",
      url: "https://www.value-plus.kr/app/main/index",
    },
    {
      id: 24,
      title: "세차관리 시스템 연리지",
      subtitle: "장애가족 협동의 힘으로<br/>발달장애인에게 일과 삶을!",
      logo: "/assets/images/page/service/web/web24.png",
      bgColor: "#EFFFE0",
      url: "https://www.yeonliji.com/",
    },
  ];
  return (
    <div className="border-t border-[#ECECEC] mt-[-2px]">
      <div className="w-full xl:max-w-[1280px] mx-auto px-5 xl:px-2 py-14 md:py-20">
        <SectionTitle
          align="left"
          label="Web"
          title={<>IT 개발 및 운영</>}
          description={
            <>
              다양한 구축 경험을 통해 적정한 기술을 활용하여
              <br />
              안정적인 서비스를 구축합니다.
            </>
          }
          colorMode="light"
        />
        <WebList items={websites} />
      </div>
    </div>
  );
}
